import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { FieldTypeEnum } from 'src/app/models/facility/field-type-enum.model';
import { AdditionalOrderMessageField } from '../../../../models/facility/additional-order-message-field.model';
import { FacilityService } from '../../../../services/api/facility.service';
import { StringUtils } from '../../../../services/utilities/string-utils.service';
import { AuthService } from '../../../../services/web/auth.service';

@Component({
    selector: 'app-add-order-msg-fields',
    templateUrl: './add-order-msg-fields.component.html',
    standalone: false
})
export class AddOrderMsgFieldsComponent implements OnInit, OnDestroy {
  @Input() public parentForm: FormGroup;
  @Input() public seededAdditionalFields: AdditionalOrderMessageField[];
  @Input() public newOrders = false;
  @Input() public refillsReorders = false;
  public localTypeEnum = FieldTypeEnum;

  public additionalFieldDefinitions: AdditionalOrderMessageField[];
  public get additionalFields() {
    return this.parentForm.controls.additionalFields as FormArray;
  }

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private facilityService: FacilityService,
    private authService: AuthService,
    private formBuilder: FormBuilder
  ) {
  }

  public ngOnInit(): void {
    if (this.seededAdditionalFields) {
      this.additionalFieldDefinitions = this.seededAdditionalFields;
      this.refreshFields(this.additionalFieldDefinitions);
    }
    else {
      this.authService.getCurrentFacility()
        .pipe(
          switchMap(id => this.facilityService.getAdditionalOrderMessageFields(id)),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(resp => {
          if (this.newOrders) {
            this.refreshFields(resp.filter(x => x.newOrders));
          } else if (this.refillsReorders) {
            this.refreshFields(resp.filter(x => x.refillsReorders));
          } else {
            this.refreshFields([]);
          }
        });
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public stringifyFields(): string {
    const notes = [];

    const additionalFields = this.parentForm.get('additionalFields').value;

    additionalFields.forEach((f, i) => {
      if (!StringUtils.isNullUndefinedOrWhitespace(f.fieldValue)) {
        notes.push(`${this.additionalFieldDefinitions[i].fieldName}: ${f.fieldValue.trim()}`);
      }
    });
    return notes.join('; ');
  }

  public addField(field: AdditionalOrderMessageField) {
    const group = this.formBuilder.group({
      fieldValue: this.fieldRequired(field)
        ? [field.fieldType === FieldTypeEnum.textBox ? '' : field?.dropListOptions[0] ?? '', Validators.required]
        : ['']
    });
    this.additionalFields.push(group);
  }

  public getFieldNameWithoutWhiteSpace(index: number) {
    return StringUtils.removeWhiteSpace(this.additionalFieldDefinitions[index].fieldName).toLowerCase();
  }

  public fieldRequired(field: AdditionalOrderMessageField): boolean {
    return (field.newOrdersRequired && this.newOrders) || (field.refillsReordersRequired && this.refillsReorders);
  }

  private refreshFields(fields: AdditionalOrderMessageField[]) {
    this.additionalFieldDefinitions = fields;
    this.additionalFields.clear();
    fields.forEach(f => this.addField(f));
  }
}
